import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Users, CheckCircle2, XCircle } from "lucide-react";

export default function JoinLeague() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | joining | joined | error | already
  const [message, setMessage] = useState("");
  const [leagueName, setLeagueName] = useState("");

  const normalizedCode = (code || "").toUpperCase().trim();
  const authLoading = user === null; // AuthProvider convention

  useEffect(() => {
    if (authLoading) return;
    // Not logged in? Redirect to register with the code pre-filled.
    if (!user) {
      navigate(`/register?code=${encodeURIComponent(normalizedCode)}`, { replace: true });
      return;
    }
    // Admin can't join
    if (user.role === "admin") {
      setStatus("error");
      setMessage("Un compte admin ne rejoint pas de ligue.");
      return;
    }
    // Already in a league?
    if (user.league_id) {
      setStatus("already");
      setLeagueName(user.league_name || "");
      return;
    }
    // Auto-join
    setStatus("joining");
    api
      .post("/league/join", { code: normalizedCode })
      .then(async ({ data }) => {
        await refreshUser();
        setLeagueName(data.league_name || "");
        setStatus("joined");
        // Redirect home shortly after
        setTimeout(() => navigate("/", { replace: true }), 1600);
      })
      .catch((e) => {
        setStatus("error");
        setMessage(formatApiError(e));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, normalizedCode]);

  return (
    <div className="max-w-md mx-auto py-10" data-testid="join-league-page">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-[color:var(--sunshine)]/20 flex items-center justify-center mb-4">
          <Users className="h-7 w-7 text-[color:var(--deep)]" />
        </div>
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Invitation</div>
        <div className="brand-wordmark text-2xl text-[color:var(--deep)] mb-1">Code : {normalizedCode || "—"}</div>

        {(authLoading || status === "joining") && (
          <div className="mt-6 flex flex-col items-center gap-3 text-slate-500 text-sm">
            <Loader2 className="h-6 w-6 animate-spin text-[color:var(--ocean)]" />
            {status === "joining" ? "On te fait rejoindre la ligue…" : "Chargement…"}
          </div>
        )}

        {status === "joined" && (
          <div className="mt-6 space-y-3" data-testid="join-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
            <div className="font-bold text-[color:var(--deep)]">
              Bienvenue dans {leagueName ? `« ${leagueName} »` : "la ligue"} !
            </div>
            <p className="text-sm text-slate-500">Redirection vers l'accueil…</p>
          </div>
        )}

        {status === "already" && (
          <div className="mt-6 space-y-4" data-testid="join-already">
            <div className="text-sm text-slate-600">
              Tu es déjà dans {leagueName ? <b>« {leagueName} »</b> : "une ligue"}. Pour rejoindre cette nouvelle
              ligue, quitte d'abord la tienne depuis ton profil.
            </div>
            <div className="flex gap-2 justify-center">
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/profil">Aller à mon profil</Link>
              </Button>
              <Button asChild className="rounded-full btn-cta">
                <Link to="/">Accueil</Link>
              </Button>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="mt-6 space-y-4" data-testid="join-error">
            <XCircle className="h-10 w-10 text-[color:var(--coral)] mx-auto" />
            <div className="text-sm text-slate-600">{message || "Impossible de rejoindre cette ligue."}</div>
            <Button asChild className="rounded-full btn-cta">
              <Link to="/">Retour à l'accueil</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
